import { SignInPrompt } from '@words-happen/ui-components';
import type React from 'react';
import { isTransient, type SessionFailure } from '../api';
import { MessageScreen, messageScreenActionClassName } from '../components';
import type { SessionHandoff } from './use-session-handoff';

/** What each failure tells the player to do. */
export const HANDOFF_MESSAGES: Readonly<Record<SessionFailure, string>> = {
  network: "We couldn't reach Words Happen. Check your connection and try again.",
  server: 'Something went wrong on our side. Try again in a moment.',
  stale: 'Signing in took too long. Try again.',
  'email-not-verified': "Your email address isn't verified yet. Open the link we sent you, then sign in again.",
  'token-invalid': 'Your sign-in has expired or was signed out elsewhere. Sign in again.',
  'origin-not-allowed': "This page can't sign you in. Open Words Happen and sign in from there.",
  unexpected: 'Something went wrong signing you in. Sign in again.',
};

export interface HandoffScreenProps {
  handoff: SessionHandoff;
}

/**
 * The session handoff in progress, or why it failed. A transient failure offers "Try again" (and a way back to the
 * form); any other offers "Sign in again", which signs out of Firebase first.
 */
export const HandoffScreen: React.FC<HandoffScreenProps> = ({ handoff: { state, complete, startOver } }) => {
  if (state.phase !== 'failed') {
    return (
      <MessageScreen title="Signing you in…" mood="focused">
        <p role="status">Getting your game ready.</p>
      </MessageScreen>
    );
  }

  const transient = isTransient(state.failure);

  return (
    <MessageScreen
      title="We couldn't sign you in"
      mood="surprised"
      action={
        transient ? (
          <>
            <button type="button" className={messageScreenActionClassName} onClick={complete}>
              Try again
            </button>
            <SignInPrompt prefix={null} action="Back to sign in" onAction={startOver} />
          </>
        ) : (
          <button type="button" className={messageScreenActionClassName} onClick={startOver}>
            Sign in again
          </button>
        )
      }
    >
      <p role="alert">{HANDOFF_MESSAGES[state.failure]}</p>
    </MessageScreen>
  );
};
